/**
 * Inventory MV2 QueryClient singleton + section prefetch/invalidation.
 */
import { makeMv2QueryClient } from "@/features/_mv2/makeQueryClient";
import { setupMv2CachePersist } from "@/features/_mv2/mv2CachePersist";
import {
  INVENTORY_DAILY_EXPENSE_ACCOUNTS_KEY,
  INVENTORY_INGREDIENT_PRICES_KEY,
  INVENTORY_CAPEX_ACCOUNTS_KEY,
  INVENTORY_SETTINGS_KEY,
  INVENTORY_STOCK_FORECAST_KEY,
  INVENTORY_CAPACITY_REPORT_KEY,
  INVENTORY_GST_MONTHLY_KEY,
  INVENTORY_CAPEX_ENTRIES_KEY,
  INVENTORY_FLAVOR_SALES_KEY,
  INVENTORY_DECOR_SALES_KEY,
  INVENTORY_MATERIAL_COSTS_KEY,
  INVENTORY_PACKAGING_COSTS_KEY,
  INVENTORY_PURCHASES_KEY,
  INVENTORY_DAILY_EXPENSE_ENTRIES_KEY,
  INVENTORY_GLOBAL_SECTION_KEYS,
  INVENTORY_SCOPED_SECTION_KEYS,
  getDefaultInventoryFilters,
  fetchDailyExpenseAccountsFromApi,
  fetchIngredientPricesFromApi,
  fetchCapexAccountsFromApi,
  fetchInventorySettingsFromApi,
  fetchStockForecastFromApi,
  fetchCapacityReportFromApi,
  fetchGstMonthlyFromApi,
  fetchCapexEntriesFromApi,
  fetchFlavorSalesFromApi,
  fetchDecorSalesFromApi,
  fetchMaterialCostsFromApi,
  fetchPackagingCostsFromApi,
  fetchPurchasesFromApi,
  fetchDailyExpenseEntriesFromApi,
} from "./inventoryQueryApi";

export const inventoryQueryClient = makeMv2QueryClient();
setupMv2CachePersist(inventoryQueryClient, "inventory");

const GLOBAL_FETCHERS = {
  [INVENTORY_DAILY_EXPENSE_ACCOUNTS_KEY]: fetchDailyExpenseAccountsFromApi,
  [INVENTORY_INGREDIENT_PRICES_KEY]: fetchIngredientPricesFromApi,
  [INVENTORY_CAPEX_ACCOUNTS_KEY]: fetchCapexAccountsFromApi,
  [INVENTORY_SETTINGS_KEY]: fetchInventorySettingsFromApi,
  [INVENTORY_STOCK_FORECAST_KEY]: fetchStockForecastFromApi,
  [INVENTORY_CAPACITY_REPORT_KEY]: fetchCapacityReportFromApi,
  [INVENTORY_GST_MONTHLY_KEY]: fetchGstMonthlyFromApi,
};

const SCOPED_FETCHERS = {
  [INVENTORY_CAPEX_ENTRIES_KEY]: fetchCapexEntriesFromApi,
  [INVENTORY_FLAVOR_SALES_KEY]: fetchFlavorSalesFromApi,
  [INVENTORY_DECOR_SALES_KEY]: fetchDecorSalesFromApi,
  [INVENTORY_MATERIAL_COSTS_KEY]: fetchMaterialCostsFromApi,
  [INVENTORY_PACKAGING_COSTS_KEY]: fetchPackagingCostsFromApi,
  [INVENTORY_PURCHASES_KEY]: fetchPurchasesFromApi,
  [INVENTORY_DAILY_EXPENSE_ENTRIES_KEY]: fetchDailyExpenseEntriesFromApi,
};

const SECTION_KEY_TO_INVALIDATE_KEY = {
  dailyExpenseAccounts: INVENTORY_DAILY_EXPENSE_ACCOUNTS_KEY,
  ingredientPrices: INVENTORY_INGREDIENT_PRICES_KEY,
  capexAccounts: INVENTORY_CAPEX_ACCOUNTS_KEY,
  settings: INVENTORY_SETTINGS_KEY,
  stockForecast: INVENTORY_STOCK_FORECAST_KEY,
  capacityReport: INVENTORY_CAPACITY_REPORT_KEY,
  gstMonthly: INVENTORY_GST_MONTHLY_KEY,
  capexEntries: INVENTORY_CAPEX_ENTRIES_KEY,
  flavorSales: INVENTORY_FLAVOR_SALES_KEY,
  decorSales: INVENTORY_DECOR_SALES_KEY,
  materialCosts: INVENTORY_MATERIAL_COSTS_KEY,
  packagingCosts: INVENTORY_PACKAGING_COSTS_KEY,
  purchases: INVENTORY_PURCHASES_KEY,
  dailyExpenseEntries: INVENTORY_DAILY_EXPENSE_ENTRIES_KEY,
};

function scopedDateKey(filters) {
  return [filters?.start_date, filters?.end_date];
}

export function invalidateInventoryGlobalSections(sectionNames) {
  const keys = sectionNames?.length
    ? sectionNames.map((name) => SECTION_KEY_TO_INVALIDATE_KEY[name]).filter(Boolean)
    : INVENTORY_GLOBAL_SECTION_KEYS;
  return Promise.all(
    keys.map((key) =>
      inventoryQueryClient.invalidateQueries({
        queryKey: [key],
      })
    )
  );
}

export function invalidateInventoryScopedSections(filters, sectionNames) {
  const dateKey = scopedDateKey(filters);
  const keys = sectionNames?.length
    ? sectionNames.map((name) => SECTION_KEY_TO_INVALIDATE_KEY[name]).filter(Boolean)
    : INVENTORY_SCOPED_SECTION_KEYS;
  return Promise.all(
    keys.map((key) =>
      inventoryQueryClient.invalidateQueries({
        queryKey: [key, ...dateKey],
      })
    )
  );
}

export function prefetchInventory(filters = getDefaultInventoryFilters()) {
  const dateKey = scopedDateKey(filters);
  const globalPrefetches = INVENTORY_GLOBAL_SECTION_KEYS.map((key) =>
    inventoryQueryClient.prefetchQuery({
      queryKey: [key],
      queryFn: ({ signal }) => GLOBAL_FETCHERS[key](signal),
    })
  );
  const scopedPrefetches = INVENTORY_SCOPED_SECTION_KEYS.map((key) =>
    inventoryQueryClient.prefetchQuery({
      queryKey: [key, ...dateKey],
      queryFn: ({ signal }) => SCOPED_FETCHERS[key](filters, signal),
    })
  );
  return Promise.all([...globalPrefetches, ...scopedPrefetches]);
}
